import { ColumnElement } from '@atoms/columnElement';
import { BasicModal } from '@atoms/modal';

import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger
} from '@components/ui/accordion';
import { Input } from '@components/ui/input';
import InputWithWarning from '@components/ui/inputWithWarning';
import { Label } from '@components/ui/label';

import type { AnalyticsModel } from '@models/analytics.model';
import { Button, DialogActions, Divider } from '@mui/joy';
import { observer } from 'mobx-react';
import type React from 'react';

import { validateFiltersInput, validateSnakeCase } from '../utils';

type EditEtlModalProps = {
  closeModal: () => void;
  openModal: boolean;
  setOpenModal: React.Dispatch<React.SetStateAction<boolean>>;
  analytics: AnalyticsModel;
  handleInputChange: (field: string, value: string) => Promise<void>;
};
const EditEtlModal = ({
  openModal,
  setOpenModal,
  closeModal,
  analytics,
  handleInputChange
}: EditEtlModalProps) => (
  <BasicModal
    open={openModal}
    setOpen={setOpenModal}
    title={'Edit the analytics ETL'}
    width="600px"
  >
    <Divider />
    <div className="mt-4 space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <ColumnElement>
          <Label htmlFor="created_at">Created At</Label>
          <Input
            className="block"
            id="created_at"
            onChange={e => {
              void handleInputChange(
                'created_at',
                new Date(e.target.value).toISOString()
              );
            }}
            placeholder="Created at"
            type="date"
            value={
              analytics.createdAt
                ? new Date(analytics.createdAt).toISOString().split('T')[0]
                : ''
            }
          />
        </ColumnElement>
        <ColumnElement>
          <Label htmlFor="updated_at">Updated At</Label>
          <Input
            className="block"
            id="updated_at"
            onChange={e => {
              void handleInputChange(
                'updated_at',
                new Date(e.target.value).toISOString()
              );
            }}
            placeholder="Updated at"
            type="date"
            value={
              analytics.updatedAt
                ? new Date(analytics.updatedAt)?.toISOString().split('T')[0]
                : ''
            }
          />
        </ColumnElement>
      </div>

      <Accordion collapsible type="single">
        <AccordionItem value="etl">
          <AccordionTrigger>ETL details</AccordionTrigger>
          <AccordionContent>
            <div className="grid grid-cols-2 gap-4 p-0.5">
              <ColumnElement>
                <Label htmlFor="schema">Schema</Label>
                <InputWithWarning
                  id="schema"
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    validateSnakeCase(e.target.value);
                    void handleInputChange('data.schema', e.target.value);
                  }}
                  placeholder="Schema"
                  type="text"
                  value={analytics.data.schema}
                  warningMessage="Caution: Changes here may affect data storage settings, possibly resulting in data loss for existing dashboards."
                />
              </ColumnElement>
              <ColumnElement>
                <Label htmlFor="rs_database">Database</Label>
                <InputWithWarning
                  id="rs_database"
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    validateSnakeCase(e.target.value);
                    void handleInputChange('data.rs_database', e.target.value);
                  }}
                  placeholder="Database in Analytics"
                  type="text"
                  value={analytics.data.rs_database}
                  warningMessage="Caution: Changes here may affect data storage settings, possibly resulting in data loss for existing dashboards."
                />
              </ColumnElement>
              <ColumnElement>
                <Label htmlFor="filters">Filters</Label>
                <Input
                  id="filters"
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    validateFiltersInput(e.target.value);
                    void handleInputChange('data.filters', e.target.value);
                  }}
                  placeholder="Remove tables from ETL"
                  type="text"
                  value={analytics.data.filters}
                />
              </ColumnElement>
              <ColumnElement>
                <Label htmlFor="column_filters">Column Filters</Label>
                <Input
                  id="column_filters"
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    validateFiltersInput(e.target.value);
                    void handleInputChange(
                      'data.column_filter',
                      e.target.value
                    );
                  }}
                  placeholder="Remove columns from table"
                  type="text"
                  value={analytics.data.column_filter}
                />
              </ColumnElement>
              <ColumnElement>
                <Label htmlFor="sentry_monitor">
                  Sentry Cronjob Monitor Slug
                </Label>
                <Input
                  id="sentry_monitor"
                  onChange={e => {
                    void handleInputChange(
                      'data.sentry_monitor',
                      e.target.value
                    );
                  }}
                  placeholder="Enter your Sentry Cronjob Monitor..."
                  type="text"
                  value={analytics.data.sentry_monitor ?? ''}
                />
              </ColumnElement>
            </div>
          </AccordionContent>
        </AccordionItem>
      </Accordion>
    </div>
    <DialogActions>
      <Button onClick={closeModal} variant="outlined">
        Cancel
      </Button>
    </DialogActions>
  </BasicModal>
);

export default observer(EditEtlModal);
